/**
 * advance-ai-home-defence.ts — v4M8.5a home-defence threat detection.
 *
 * Pure detection helper consumed by v4M8.5b's per-empire recall
 * dispatch. Encodes the two-tier threat model from `docs/AI.md §5.5`:
 *
 *   - `critical`: at least one non-team enemy ship visible (via
 *     team-vision) in the empire's homeworld system. AI should
 *     immediately recall `recallFraction` of its foreign-deployed
 *     combat ships.
 *   - `warning`: at least one non-team enemy ship visible in a
 *     system jump-adjacent to the homeworld. Brutal-only
 *     (`preEmptiveRecallEnabled`) recalls one task force pre-
 *     emptively. Non-Brutal empires ignore warning-tier — they react
 *     only to ships actually arriving.
 *   - `none`: no visible threat to the home theatre.
 *
 * Empire's homeworld = first owned planet's parent system. Matches
 * the `pickHomeworldPosition` convention used elsewhere in
 * advance-ai.ts; if the empire's owned-planet ordering changes mid-
 * game (e.g. annex / loss flip) the "homeworld" tracks the
 * lowest-id owned planet. Empires with no planets return tier
 * `'none'` (nothing to defend).
 *
 * Reads enemy strength via the v4M8.2a `enemyStrength` helper which
 * already gates on team-vision, skips dead hulls + teammates +
 * neutrals, and weights by per-ship damagePotential × healthFactor.
 * `threatScore` carries this through so the dispatch pass can
 * proportion the recall to the perceived threat (not just binary).
 *
 * v4M8.5a strict-parity — exported + tested, no consumer reads it
 * yet. v4M8.5b wires it into the per-empire loop.
 */

import { enemyStrength } from './advance-ai-targeting';
import type { Empire, GameState, SystemId } from '../state/types/index';

/** Tier returned by `detectHomeDefenceThreat`. */
export type HomeDefenceTier = 'none' | 'warning' | 'critical';

export interface HomeDefenceThreat {
  tier: HomeDefenceTier;
  /**
   * Sum of `enemyStrength` contributions for the relevant system(s):
   * homeworld alone on `'critical'`; sum across jump-adjacent systems
   * on `'warning'`; 0 on `'none'`. Allows the dispatch pass to scale
   * the recall response — a single Scout in the homeworld is
   * `critical` tier but doesn't warrant the same recall fraction as
   * a full enemy battle group.
   */
  threatScore: number;
  /**
   * The empire's homeworld system id (or null when the empire has no
   * owned planets). Surfaced so the dispatch pass doesn't have to
   * re-resolve it.
   */
  homeworldSystemId: SystemId | null;
}

/**
 * Detect the empire's home-defence threat tier this tick.
 *
 * Pure — no state mutation. O(adjacent-systems × team-vision-rows)
 * per call; cheap at typical galaxy scales (3-5 adjacent systems,
 * ~20-50 team-vision records).
 */
export function detectHomeDefenceThreat(
  state: GameState,
  empire: Empire,
): HomeDefenceThreat {
  const homeworldSystemId = findHomeworldSystem(state, empire);
  if (homeworldSystemId === null) {
    return { tier: 'none', threatScore: 0, homeworldSystemId: null };
  }

  // Critical: any visible non-team enemy in the homeworld system.
  // Strict > 0 — `enemyStrength` already returns 0 when no visible
  // threat exists.
  const homeStrength = enemyStrength(state, empire, homeworldSystemId);
  if (homeStrength > 0) {
    return { tier: 'critical', threatScore: homeStrength, homeworldSystemId };
  }

  // Warning: visible enemy in any jump-adjacent system.
  const homeSys = state.systems.get(homeworldSystemId);
  if (!homeSys) {
    return { tier: 'none', threatScore: 0, homeworldSystemId };
  }
  let warningTotal = 0;
  for (const adjId of homeSys.adjacentSystemIds) {
    warningTotal += enemyStrength(state, empire, adjId);
  }
  if (warningTotal > 0) {
    return { tier: 'warning', threatScore: warningTotal, homeworldSystemId };
  }

  return { tier: 'none', threatScore: 0, homeworldSystemId };
}

/**
 * Empire's homeworld = parent system of the first owned planet
 * resolvable from `empire.planetIds`. Matches the convention used in
 * `advance-ai.ts:pickHomeworldPosition` so the recall pass agrees
 * with the dispatch pass on what "home" means. Returns null when
 * the empire owns no planets.
 */
function findHomeworldSystem(state: GameState, empire: Empire): SystemId | null {
  for (const pid of empire.planetIds) {
    const planet = state.planets.get(pid);
    if (planet) return planet.systemId;
  }
  return null;
}
